import React, { useEffect, useState } from "react";
import NotesContainer from "../Notes/NotesContainer";
import { collection, query, where, orderBy, limit, onSnapshot, startAfter, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { incrementDailyReads } from "../../logsComponents/incrementDailyReads";

function QuickNotesContainerAll(props) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientsWithQuickNotes, setClientsWithQuickNotes] = useState([]);
  const [, setReadCount] = useState(0);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const notesRef = collection(db, "notes");
    const q = query(
      notesRef,
      where("noteType", "==", "quickNote"),
      orderBy("effectiveDateTime", "desc"),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const notesData = [];
      const clientMap = new Map();
      querySnapshot.forEach((d) => {
        const note = { id: d.id, ...d.data() };
        notesData.push(note);
        if (note.clientId && !clientMap.has(note.clientId)) {
          clientMap.set(note.clientId, {
            id: note.clientId,
            firstName: note.clientFirstName || "",
            lastName: note.clientLastName || "",
            profilePicture: note.clientProfilePicture || "",
          });
        }
      });

      setLoading(false);
      setNotes(notesData);
      setClientsWithQuickNotes(Array.from(clientMap.values()));
      const last = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      setLastDoc(last);
      setHasMore(!!last);
      setReadCount((prevCount) => prevCount + querySnapshot.size);
      try { incrementDailyReads("notes_quick", querySnapshot.size + clientMap.size); } catch {}
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Removed extra Behaviour Concerns listener to avoid additional reads

  const loadMore = async () => {
    if (!lastDoc) return;
    setLoadingMore(true);
    const notesRef = collection(db, "notes");
    const q = query(
      notesRef,
      where("noteType", "==", "quickNote"),
      orderBy("effectiveDateTime", "desc"),
      startAfter(lastDoc),
      limit(50)
    );
    const snap = await getDocs(q);
    const moreNotes = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setNotes((prev) => [...prev, ...moreNotes]);

    // Merge clients list
    setClientsWithQuickNotes((prev) => {
      const map = new Map(prev.map((c) => [c.id, c]));
      for (const n of moreNotes) {
        if (n.clientId && !map.has(n.clientId)) {
          map.set(n.clientId, {
            id: n.clientId,
            firstName: n.clientFirstName || "",
            lastName: n.clientLastName || "",
            profilePicture: n.clientProfilePicture || "",
          });
        }
      }
      return Array.from(map.values());
    });

    const last = snap.docs[snap.docs.length - 1] || null;
    setLastDoc(last);
    setHasMore(!!last);
    setLoadingMore(false);
    try { incrementDailyReads("notes_quick", moreNotes.length); } catch {}
  };

  const filteredNotes = selectedClients.length
    ? notes.filter((note) =>
        selectedClients.some((client) => client.id === note.clientId)
      )
    : notes;

  return (
    <div className="notes-container-global">
      <div
        style={{
          width: "100%",
          paddingLeft: "90px",
          paddingRight: "45px",
          marginTop: "20px",
        }}
      >
        <Autocomplete
          multiple
          options={clientsWithQuickNotes}
          getOptionLabel={(client) => `${client.firstName} ${client.lastName}`}
          onChange={(event, value) => setSelectedClients(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Filter by Clients"
              placeholder="Select Clients"
            />
          )}
          style={{ marginBottom: "20px" }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center" }}>
          <CircularProgress />
          <p>Loading, please wait...</p>
        </div>
      ) : (
        filteredNotes.map((note, index) => {
          const dateOptions = {
            day: "numeric",
            month: "short",
            year: "numeric",
          };

          const dateOptions2 = {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          };

          const timeOptions = {
            hour: "2-digit",
            minute: "2-digit",
          };

          const formattedTimestamp = note.createdAt
            ? new Intl.DateTimeFormat("en-EN", dateOptions2).format(
                note.createdAt.toDate()
              )
            : "";

          const formattedLastModification = note.updateAt
            ? new Intl.DateTimeFormat("en-EN", dateOptions2).format(
                note.updateAt.toDate()
              )
            : "";

          const formattedDate = note.effectiveDateTime
            ? new Intl.DateTimeFormat("en-EN", dateOptions).format(
                note.effectiveDateTime.toDate()
              )
            : "";

          const formattedTime = note.effectiveDateTime
            ? new Intl.DateTimeFormat("en-EN", timeOptions).format(
                note.effectiveDateTime.toDate()
              )
            : "";

          return (
            <div key={note.id || index} style={{ paddingBottom: "15px" }}>
              <NotesContainer
                key={note.id}
                noteContent={note.noteContent}
                effectiveDate={note.effectiveDateTime}
                noteDate={formattedDate}
                noteTime={formattedTime}
                doneBy={note.doneBy}
                timeStamp={formattedTimestamp}
                lastModification={formattedLastModification}
                clientPic={note.clientProfilePicture}
                clientFirstName={note.clientFirstName}
                clientLastName={note.clientLastName}
                noteId={note.id}
                {...note}
              />
            </div>
          );
        })
      )}
      {!loading && hasMore && (
        <div style={{ textAlign: "center", margin: "8px 0 16px" }}>
          <button onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

export default QuickNotesContainerAll;
